import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Phone, 
  Flame, 
  Snowflake, 
  Wind, 
  Zap, 
  Wrench,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const HeatingCoolingServicesPage = () => {
  const navigate = useNavigate();

  const serviceCategories = [
    {
      title: "Heating Services",
      icon: Flame,
      color: "from-orange-500 to-red-500",
      iconBg: "bg-orange-500",
      services: [
        "Furnace Installation",
        "Furnace Replacement",
        "Furnace Repair",
        "Heat Pump Installation / Heat Pump Repair",
        "Heating System Maintenance",
        "Thermostat Installation & Repair",
        "Emergency Heating Services"
      ]
    },
    {
      title: "Cooling Services",
      icon: Snowflake,
      color: "from-blue-400 to-cyan-500",
      iconBg: "bg-blue-500",
      services: [
        "Air Conditioning Installation",
        "Air Conditioning Replacement",
        "Air Conditioning Repair",
        "Central Air Conditioning Systems",
        "Cooling System Maintenance",
        "Thermostat Installation & Repair",
        "Emergency Cooling Services"
      ]
    },
    {
      title: "Indoor Air Quality Services",
      icon: Wind,
      color: "from-teal-400 to-green-500",
      iconBg: "bg-teal-500",
      services: [
        "Air Duct Cleaning",
        "Air Purification Systems",
        "Humidity Control Systems",
        "UV Light Installation",
        "Ventilation Systems Installation"
      ]
    },
    {
      title: "Energy Efficiency Services",
      icon: Zap,
      color: "from-yellow-400 to-orange-500",
      iconBg: "bg-yellow-500",
      services: [
        "Energy Audits",
        "Insulation Services",
        "Smart Thermostat",
        "Zoning Systems Installation",
        "Preventive Maintenance Plans"
      ]
    },
    {
      title: "Additional Services",
      icon: Wrench,
      color: "from-purple-400 to-pink-500",
      iconBg: "bg-[#622B62]",
      services: [
        "Seasonal Maintenance Checkups",
        "Emergency Repairs",
        "Custom HVAC Solutions",
        "Consultation Services"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF7]">


      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#622B62] text-white py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#7A3D7A] rounded-full opacity-20" />
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center">
              <Flame size={32} />
            </div>
            <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
              <Snowflake size={32} />
            </div>
          </div>
          <h1 className="romano-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Heating & Cooling Services
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Comprehensive HVAC solutions for your home and business. From installation to maintenance, we've got you covered.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {serviceCategories.map((category, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Category Header */}
              <div className={`bg-gradient-to-r ${category.color} p-6 text-white`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <category.icon size={28} />
                  </div>
                  <h2 className="text-xl font-bold">{category.title}</h2>
                </div>
              </div>
              
              {/* Services List */}
              <div className="p-6">
                <ul className="space-y-3">
                  {category.services.map((service, serviceIndex) => (
                    <li key={serviceIndex} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full ${category.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Check size={12} className="text-white" />
                      </div>
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <h2 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and estimate. Our expert team is ready to help with all your HVAC needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                  <Phone size={22} className="mr-2" />
                  Call Now: {companyInfo.phone}
                </Button>
              </a>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                onClick={() => navigate('/#contact')}
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-24 w-24 object-contain rounded-lg"
            />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano</h3>
              <p className="text-white/70 text-sm">Heating & Cooling</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            &copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeatingCoolingServicesPage;
